import { useCallback, useEffect, useRef, useState } from 'react';
import { apiService } from '../../../services/api';
import { buildDryRunParams } from './alertPayload';

/** How long the threshold has to stop changing before a dry run is sent. */
const DEBOUNCE_MS = 600;

/**
 * The live match count and per-channel previews behind the alert wizard.
 *
 * **Mounted once, by the wizard, not per step.** Each step mounting its own
 * copy meant navigating 2 → 3 → 2 fired three full evaluations of an unchanged
 * spec — the cost the debounce exists to avoid — and left step 4 without a
 * result at all, so the volume-guard banners could never fire even though the
 * guard is only configurable there.
 *
 * **Debouncing is this story's problem, not the API's.** Every call runs a real
 * evaluation against the structured-data service, so a keystroke-per-request
 * threshold input would put a query on the wire for "1", "12" and "125" on the
 * way to "1250".
 *
 * Three things beyond the debounce matter, and all three are about never
 * showing a number that is no longer true:
 *
 *  - **Responses can land out of order.** Requests are compared against the
 *    newest *issued* sequence, not the newest *applied* one. Anchoring to
 *    "applied" let an early-resolving request clear `loading` while a newer one
 *    was still in flight, presenting a superseded count as settled.
 *  - **A reset must invalidate what is in flight.** When the spec goes
 *    incomplete the sequence is bumped, so a request already on the wire
 *    cannot land afterwards and repopulate a count that was just cleared.
 *  - **Unmounting stops everything.** Leaving the wizard mid-request must not
 *    set state on a dead hook.
 */
export const useAlertDryRun = (formData, { enabled = true } = {}) => {
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  // The newest sequence *issued*. A response is only applied if it is still
  // the newest — see the note above on why "applied" was the wrong anchor.
  const runSeqRef = useRef(0);
  const mountedRef = useRef(true);
  // The in-flight request, so a superseded or unmounted run stops costing a
  // real evaluation on the structured-data service rather than just having its
  // response ignored.
  const abortRef = useRef(null);

  // **Set on mount, not just cleared on unmount.** React 18 StrictMode — on in
  // `src/index.js` — mounts, runs effects, runs cleanups, then runs the effects
  // again. A ref only ever set to false stayed false for the component's whole
  // life, so every response was discarded and the live count sat on
  // "Evaluating against current data…" forever. Development only, which is
  // exactly where nobody would see a green test suite catch it.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  const params = buildDryRunParams(formData);
  // Objects are rebuilt every render, so they cannot be dependencies directly.
  // Serialising compares by value instead.
  const paramsKey = params.alertSpec ? JSON.stringify(params) : '';

  const run = useCallback(async (seq, body) => {
    setLoading(true);
    setError(null);
    // Abandon whatever is still on the wire — its answer is already stale.
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const response = await apiService.dryRunUnsavedAlert(body, {
        signal: controller.signal,
      });
      if (!mountedRef.current || seq !== runSeqRef.current) return null;
      setResult(response);
      return response;
    } catch (err) {
      if (!mountedRef.current || seq !== runSeqRef.current) return;
      // A run we abandoned on purpose, or one that ran out of patience.
      if (err?.name === 'AbortError') return;
      if (err?.isTimeout) {
        setError('The evaluation took too long to answer. Narrow the datasets or the window and try again.');
        setResult(null);
        return;
      }
      // Validation refusals are written for the person who submitted them, so
      // the message is surfaced as-is rather than reworded.
      const detail = err?.responseData?.detail;
      const message = typeof detail === 'string'
        ? detail
        : Array.isArray(detail)
          ? (detail[0]?.msg || err.message)
          : err?.message || 'Dry run failed';
      setError(message);
      setResult(null);
      return null;
    } finally {
      if (mountedRef.current && seq === runSeqRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled || !paramsKey) {
      // Bump the sequence so anything already on the wire is invalidated and
      // cannot repopulate the result after this reset — and abort it, so it
      // stops costing a real evaluation rather than merely having its answer
      // discarded. Invalidating without aborting contradicted this hook's own
      // stated intent.
      runSeqRef.current += 1;
      if (abortRef.current) abortRef.current.abort();
      setResult(null);
      setError(null);
      setLoading(false);
      return undefined;
    }
    runSeqRef.current += 1;
    const seq = runSeqRef.current;
    const body = JSON.parse(paramsKey);
    // Set as soon as the run is *scheduled*, not when it fires. Setting it
    // inside `run` left the previous count on screen undimmed and without the
    // "updating…" hint for the whole debounce window, reading as settled when
    // it was already superseded.
    setLoading(true);
    const timer = setTimeout(() => run(seq, body), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [enabled, paramsKey, run]);

  /**
   * Run now, bypassing the debounce.
   *
   * Exists so the manual Dry run button drives the same evaluation the panel
   * shows. Firing its own request meant two evaluations and two sources of
   * truth: a timed-out hook run could leave the error banner up while the
   * button toasted a successful count.
   */
  const runNow = useCallback(async () => {
    if (!paramsKey) return null;
    runSeqRef.current += 1;
    const seq = runSeqRef.current;
    return run(seq, JSON.parse(paramsKey));
  }, [paramsKey, run]);

  return { result, error, loading, ready: !!paramsKey, runNow };
};

export default useAlertDryRun;
